/**
 * A2 — utilitários de imagem client-side.
 *
 * `compressImage(file, maxEdge, quality) → Promise<Blob>`
 *
 * Pipeline:
 *   1. Para HEIC/HEIF, devolve o arquivo original (o browser não abre
 *      nativo na maioria dos casos; o backend já converte via pillow-heif).
 *   2. Para JPEG/PNG/WebP, abre via `createImageBitmap` (fallback
 *      `Image + FileReader` para Safari antigo).
 *   3. Desenha em `<canvas>` redimensionado pra longest-edge ≤ `maxEdge`.
 *   4. `canvas.toBlob('image/jpeg', quality)` → Blob.
 *
 * Sem nova dependência: tudo via APIs nativas do browser. A redução
 * client-side reduz custo da extração na A1 (imagem chega pequena) e
 * a banda.
 */

const HEIC_MIMES = new Set(["image/heic", "image/heif"]);

const HEIC_EXT_RE = /\.(heic|heif)$/i;


/**
 * Decide se o arquivo deve passar sem compressão.
 * HEIC/HEIF: a maioria dos browsers não decodifica → backend cuida.
 */
export function shouldBypassCompression(file) {
  if (!file) return true;
  const mime = (file.type || "").toLowerCase();
  if (HEIC_MIMES.has(mime)) return true;
  if (!mime && file.name && HEIC_EXT_RE.test(file.name)) return true;
  return false;
}


/**
 * Lê um File como `HTMLImageElement` via `FileReader + Image.src`.
 * Fallback pra Safari antigo onde `createImageBitmap` falha em alguns
 * casos (ex.: EXIF strip).
 */
function _readAsImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e instanceof Error ? e : new Error("image load"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("file read"));
    reader.readAsDataURL(file);
  });
}


/**
 * Comprime imagem client-side. Para HEIC/HEIF, devolve o arquivo
 * intacto (vira Blob via slice — o backend converte).
 */
export async function compressImage(file, maxEdge = 1024, quality = 0.85) {
  if (!file) throw new Error("file vazio");
  if (shouldBypassCompression(file)) {
    // HEIC: passa direto. Em browser, File já é um Blob.
    return file;
  }

  // Lê via createImageBitmap (rápido, sem string-encoding). Fallback
  // pra Image quando bitmap falha (Safari/iOS antigos com EXIF rotation).
  let width;
  let height;
  let drawable;
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(file);
      width = bmp.width;
      height = bmp.height;
      drawable = bmp;
    } catch {
      drawable = null;
    }
  }
  if (!drawable) {
    const img = await _readAsImage(file);
    width = img.naturalWidth;
    height = img.naturalHeight;
    drawable = img;
  }

  // Calcula tamanho final preservando aspect ratio.
  const longest = Math.max(width, height);
  let outW = width;
  let outH = height;
  if (longest > maxEdge) {
    const ratio = maxEdge / longest;
    outW = Math.round(width * ratio);
    outH = Math.round(height * ratio);
  }

  // Desenha em canvas e exporta JPEG.
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d context indisponível");
  ctx.drawImage(drawable, 0, 0, outW, outH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob retornou null"))),
      "image/jpeg",
      quality,
    );
  });
  return blob;
}
